/**
 * Terreno da arena — um modelo só, válido em qualquer modo.
 *
 * Água, lava, gelo e lama nasceram dentro da Batalha Infinita, e por isso o
 * código morava em `lib/infinite/`. O problema disso não era arrumação: era que
 * o resto do jogo não tinha como usar o terreno sem depender do modo solo, e um
 * mesmo lago se comportava de um jeito ali e de nenhum jeito nos outros modos.
 *
 * Este módulo é a resposta. Ele guarda **tudo o que o terreno é** — o que cada
 * piso faz com um tanque, quanto tempo o fogo gruda, como uma arena é sorteada
 * e desenhada — sem depender de React, de `three` ou de qualquer store. Com
 * isso:
 *
 *  - o cliente usa para o jogador, para os bots e para os jogadores remotos;
 *  - o servidor Colyseus usa para gerar a mesma arena e cobrar o mesmo dano;
 *  - todo modo (infinito, Battle Royale solo, multijogador, BR multijogador)
 *    passa pelo mesmo `planArenaTerrain` e só muda o **perfil**, nunca a regra.
 *
 * A tabela `MODE_PROFILES` é o único lugar onde um modo difere de outro. Se um
 * dia a lava tiver de sumir do multijogador, é uma linha — e nada mais no jogo
 * precisa saber que isso aconteceu.
 *
 * Duas decisões de projeto sustentam o resto:
 *
 * 1. **Zonas são círculos.** Polígonos dariam formas mais bonitas e um teste de
 *    contenção muito mais caro por frame. Vários círculos sobrepostos já
 *    produzem lagos de contorno orgânico pelo custo de uma subtração e uma
 *    comparação.
 *
 * 2. **A consulta é um lookup em máscara, não um teste de geometria.** O
 *    terreno é rasterizado uma vez numa grade; `sample` vira um acesso O(1) a
 *    um `Uint8Array`. Em arena de 70 unidades são 1.225 células — alguns KB — e
 *    o custo por frame é constante independentemente de quantas zonas a arena
 *    tenha.
 *
 * Não há altura nem pontes aqui: tanques continuam em y=0. Elevação exigiria
 * mexer no eixo vertical de colisão, spawn e IA ao mesmo tempo, e o ganho não
 * paga o risco de deixar um tanque preso no ar.
 */

// ============================================
// PISOS
// ============================================

export type SurfaceType = "ground" | "water" | "lava" | "ice" | "mud";

export type HazardType = Exclude<SurfaceType, "ground">;

export interface HazardZone {
  type: HazardType;
  x: number;
  z: number;
  radius: number;
}

/** Círculo que a geração nunca cobre — spawn de jogador, de bot, de item. */
export interface SafeSpot {
  x: number;
  z: number;
  radius: number;
}

export interface SurfaceProps {
  /** Multiplicador de velocidade ao atravessar. */
  speedMul: number;
  /** Dano por segundo enquanto estiver dentro. */
  damagePerSecond: number;
  /**
   * Tração: 1 responde na hora, valores baixos derrapam. Consumido como
   * suavização da direção de movimento, não como perda de velocidade.
   */
  traction: number;
  /**
   * Quanto tempo, em segundos, o tanque leva para perder quase toda a
   * velocidade depois que o comando é solto.
   *
   * Este é o número que separa "escorregar" de "frear". Antes o freio era um
   * fator fixo por frame igual para todo piso: o gelo tirava a direção mas
   * parava o tanque no mesmo tempo que o asfalto. Agora cada piso diz quanto
   * tempo o impulso sobrevive, e o gelo é o único que o segura.
   */
  glideSeconds: number;
}

export const SURFACE_PROPS: Record<SurfaceType, SurfaceProps> = {
  ground: { speedMul: 1, damagePerSecond: 0, traction: 1, glideSeconds: 0.3 },
  // Água não mata — deixa lento e exposto. Numa arena onde a criança já pode
  // morrer para oito tanques, um lago letal seria punição sem leitura.
  // A massa de água também engole o impulso: sem comando, para quase na hora.
  water: { speedMul: 0.55, damagePerSecond: 0, traction: 0.9, glideSeconds: 0.2 },
  // Lava é o atalho que custa vida: a decisão mais interessante do conjunto.
  // O tique é lento o bastante para dar tempo de sair.
  lava: { speedMul: 0.72, damagePerSecond: 10, traction: 1, glideSeconds: 0.3 },
  // Gelo: mais rápido do que o chão firme, quase sem autoridade de direção e
  // com o impulso durando mais de dois segundos. Soltar o comando não freia —
  // o tanque segue para onde já ia até a inércia acabar.
  ice: { speedMul: 1.12, damagePerSecond: 0, traction: 0.16, glideSeconds: 2.2 },
  // Lama é o oposto exato do gelo: agarra. Sem comando o tanque atola no lugar.
  mud: { speedMul: 0.62, damagePerSecond: 0, traction: 0.8, glideSeconds: 0.1 },
};

/**
 * Fator de velocidade que sobra depois de `dt` segundos sem comando.
 *
 * Convenção: em `glideSeconds` resta 5% do impulso. Ser função do tempo — e não
 * do frame — é o que faz o gelo escorregar igual a 30 e a 120 FPS; a versão
 * anterior multiplicava por frame, então quem jogava a 28 FPS (celular
 * carregado) escorregava menos da metade do que quem jogava a 60.
 */
export function glideDecay(glideSeconds: number, dt: number): number {
  if (glideSeconds <= 0) return 0;
  return Math.pow(0.05, dt / glideSeconds);
}

/**
 * Suavização exponencial independente de frame-rate.
 *
 * `perFrame` é o fator calibrado a 60 FPS (0..1). Devolve o fator equivalente
 * para o `dt` real. Usado pela tração e pela aceleração.
 */
export function frameRateAdjusted(perFrame: number, dt: number): number {
  if (perFrame >= 1) return 1;
  if (perFrame <= 0) return 0;
  return 1 - Math.pow(1 - perFrame, dt * 60);
}

// ============================================
// ESTADO QUE O TERRENO DEIXA NO TANQUE
// ============================================

/**
 * Sem estado o terreno é binário — dentro da lava dói, fora não dói, e sair da
 * poça apaga o problema no mesmo frame. O resultado é que a lava vira um piso
 * ruim em vez de uma ameaça, e a água não deixa marca nenhuma.
 *
 * As três regras abaixo transformam os perigos num vocabulário que conversa
 * entre si:
 *
 *  1. **A lava gruda.** Sair da poça não apaga o fogo: o tanque continua
 *     queimando alguns segundos, com dano menor.
 *  2. **A água apaga.** Entrar na água ou na lama mata o fogo na hora.
 *  3. **Molhado protege.** Enquanto encharcado, a lava cobra bem menos — molhar
 *     antes de cruzar é uma tática de verdade.
 *
 * Lógica pura de propósito: é o núcleo de uma mecânica de dano, e dano é onde
 * um erro sutil estraga a partida sem deixar rastro. Por ser pura, o mesmo
 * código roda no tanque do jogador, no bot local e no servidor multijogador —
 * que é o que garante que o fogo dure o mesmo tanto para todo mundo.
 */

/** Quanto tempo o tanque continua em chamas depois de deixar a lava. */
export const BURNING_MS = 3500;
/** Quanto tempo o tanque fica encharcado depois de sair da água. */
export const SOAKED_MS = 6000;
/**
 * Quanto tempo a lama fica grudada no casco depois de sair do charco.
 *
 * Mais longa que a água de propósito: água escorre, lama seca e continua ali.
 */
export const MUDDY_MS = 8000;
/** Dano por segundo das chamas fora da lava — bem menor que o da poça. */
export const BURNING_DPS = 4;
/** Fração do dano da lava que atinge um tanque encharcado. */
export const SOAKED_LAVA_MULT = 0.3;
/**
 * Fração do dano da lava que atinge um tanque enlameado.
 *
 * Lama protege — é casca úmida sobre o casco — mas menos que estar encharcado,
 * porque ela racha no calor. Ficar entre 1 e `SOAKED_LAVA_MULT` é o que faz
 * valer a pena procurar água mesmo já estando sujo de lama.
 */
export const MUDDY_LAVA_MULT = 0.55;

export interface SurfaceStatus {
  /** Timestamp até quando o tanque está em chamas (0 = não está). */
  burningUntil: number;
  /** Timestamp até quando o tanque está encharcado. */
  soakedUntil: number;
  /** Timestamp até quando o tanque está coberto de lama. */
  muddyUntil: number;
}

export const NEUTRAL_STATUS: Readonly<SurfaceStatus> = Object.freeze({
  burningUntil: 0,
  soakedUntil: 0,
  muddyUntil: 0,
});

/** Acontecimentos do tick, para o VFX e o áudio reagirem. */
export type StatusEvent = "ignited" | "extinguished" | "soaked" | "muddied" | "washed";

export interface StatusTick {
  status: SurfaceStatus;
  /** Dano por segundo a aplicar neste instante. */
  damagePerSecond: number;
  events: StatusEvent[];
}

export interface StatusModifiers {
  /** Fração do dano de terreno ignorada (carta de escudo térmico). 0..1 */
  hazardResist: number;
}

const NO_MODS: StatusModifiers = { hazardResist: 0 };

/**
 * Avança o estado do tanque um instante.
 *
 * Puro e sem alocação supérflua: roda a cada frame para cada tanque em cena.
 * Recebe e devolve o estado em vez de mutar, para que os testes possam
 * encadear ticks sem depender de ordem de execução.
 */
export function tickSurfaceStatus(
  status: SurfaceStatus,
  surface: SurfaceType,
  now: number,
  mods: StatusModifiers = NO_MODS,
): StatusTick {
  const events: StatusEvent[] = [];
  let { burningUntil, soakedUntil, muddyUntil } = status;

  const wasBurning = now < burningUntil;
  const wasSoaked = now < soakedUntil;
  const wasMuddy = now < muddyUntil;

  // ---- O que a superfície faz ao tanque ----
  if (surface === "water") {
    soakedUntil = now + SOAKED_MS;
    if (!wasSoaked) events.push("soaked");
    // Água também lava a lama: sair do charco pelo lago devolve o tanque limpo.
    // Sem isto, "molhado" e "enlameado" empilhariam para sempre e o casco
    // ficaria sujo o resto da corrida.
    if (wasMuddy) {
      muddyUntil = 0;
      events.push("washed");
    }
    // A água vence o fogo, sempre e imediatamente.
    if (wasBurning) {
      burningUntil = 0;
      events.push("extinguished");
    }
  } else if (surface === "mud") {
    // Lama suja em vez de molhar. É o mesmo gesto — atravessar um piso úmido —
    // mas deixa outra marca no tanque e outro rastro no chão.
    muddyUntil = now + MUDDY_MS;
    if (!wasMuddy) events.push("muddied");
    // Charco é lama encharcada: também apaga o fogo.
    if (wasBurning) {
      burningUntil = 0;
      events.push("extinguished");
    }
  } else if (surface === "lava") {
    // Molhado ou enlameado não pega fogo: é a janela que recompensa quem passou
    // pela água (ou pelo charco) antes de encarar a poça.
    if (!wasSoaked && !wasMuddy) {
      burningUntil = now + BURNING_MS;
      if (!wasBurning) events.push("ignited");
    }
  }

  const isSoakedNow = now < soakedUntil;
  const isMuddyNow = now < muddyUntil;
  const isBurningNow = now < burningUntil;

  // ---- Dano ----
  // Dentro da lava vale o dano da poça; fora dela, só as chamas residuais.
  // Nunca somam: quem está na lava já está no pior caso.
  let damagePerSecond = 0;
  if (surface === "lava") {
    // Encharcado ganha a proteção cheia; enlameado, a parcial. Quando os dois
    // valem, vale o melhor — o jogador nunca é punido por ter se sujado antes.
    const shield = isSoakedNow ? SOAKED_LAVA_MULT : isMuddyNow ? MUDDY_LAVA_MULT : 1;
    damagePerSecond = SURFACE_PROPS.lava.damagePerSecond * shield;
  } else if (isBurningNow) {
    damagePerSecond = BURNING_DPS;
  }

  damagePerSecond *= 1 - Math.min(1, Math.max(0, mods.hazardResist));

  return {
    status: { burningUntil, soakedUntil, muddyUntil },
    damagePerSecond,
    events,
  };
}

export function isBurning(status: SurfaceStatus, now: number): boolean {
  return now < status.burningUntil;
}

export function isSoaked(status: SurfaceStatus, now: number): boolean {
  return now < status.soakedUntil;
}

export function isMuddy(status: SurfaceStatus, now: number): boolean {
  return now < status.muddyUntil;
}

/** Molhado de qualquer jeito — água ou lama. É o que o fogo não suporta. */
export function isWet(status: SurfaceStatus, now: number): boolean {
  return isSoaked(status, now) || isMuddy(status, now);
}

// ============================================
// MÁSCARA DE CONSULTA
// ============================================

const CELL_SIZE = 2;
const TYPE_ORDER: readonly SurfaceType[] = ["ground", "water", "lava", "ice", "mud"];

/**
 * Terreno rasterizado: a forma de perguntar "que piso tem aqui?" sessenta vezes
 * por segundo, por tanque, sem varrer zona nenhuma.
 *
 * Instanciável de propósito. O cliente mantém uma máscara viva da arena atual;
 * o servidor mantém uma por sala. Antes o estado era um singleton de módulo, o
 * que funcionava enquanto só existia um jogo local — e impediria o servidor de
 * hospedar duas partidas ao mesmo tempo.
 */
export class SurfaceMask {
  readonly zones: readonly HazardZone[];
  private readonly mask: Uint8Array;
  private readonly gridCount: number;
  private readonly origin: number;

  constructor(zones: readonly HazardZone[], arenaSize: number) {
    this.zones = zones.slice();
    this.gridCount = Math.ceil(arenaSize / CELL_SIZE) + 2;
    this.origin = -(this.gridCount * CELL_SIZE) / 2;
    this.mask = new Uint8Array(this.gridCount * this.gridCount);

    for (let i = 0; i < this.gridCount; i++) {
      const x = this.origin + (i + 0.5) * CELL_SIZE;
      for (let j = 0; j < this.gridCount; j++) {
        const z = this.origin + (j + 0.5) * CELL_SIZE;

        // A última zona que contém a célula vence — zonas posteriores são
        // desenhadas por cima, então a consulta bate com o que se vê.
        let type: SurfaceType = "ground";
        for (const zone of zones) {
          const dx = x - zone.x;
          const dz = z - zone.z;
          if (dx * dx + dz * dz <= zone.radius * zone.radius) type = zone.type;
        }
        this.mask[i * this.gridCount + j] = TYPE_ORDER.indexOf(type);
      }
    }
  }

  sample(x: number, z: number): SurfaceType {
    const i = Math.floor((x - this.origin) / CELL_SIZE);
    const j = Math.floor((z - this.origin) / CELL_SIZE);
    if (i < 0 || j < 0 || i >= this.gridCount || j >= this.gridCount) return "ground";
    return TYPE_ORDER[this.mask[i * this.gridCount + j]] ?? "ground";
  }

  props(x: number, z: number): SurfaceProps {
    return SURFACE_PROPS[this.sample(x, z)];
  }
}

/** Teste direto contra as zonas, sem máscara — para spawn e geração. */
export function isInsideHazard(
  x: number,
  z: number,
  zones: readonly HazardZone[],
  margin = 0,
): boolean {
  for (const zone of zones) {
    const dx = x - zone.x;
    const dz = z - zone.z;
    const r = zone.radius + margin;
    if (dx * dx + dz * dz <= r * r) return true;
  }
  return false;
}

/**
 * Empurra um ponto para fora de qualquer perigo, mantendo-o dentro da arena.
 *
 * Usado no spawn: um tanque que nasce dentro da lava perde vida por um sorteio,
 * antes mesmo de encostar no controle — e um bot nessa situação morre sozinho,
 * creditando ao jogador um abate que não foi dele. Devolve `null` quando não
 * existe saída (arena tomada), e aí quem chamou decide o que fazer.
 */
export function pushOutOfHazards(
  x: number,
  z: number,
  zones: readonly HazardZone[],
  arenaSize: number,
  margin = 1,
): { x: number; z: number } | null {
  if (!isInsideHazard(x, z, zones, margin)) return { x, z };

  const edge = arenaSize / 2 - 3;
  for (let step = 1; step <= 12; step++) {
    for (let a = 0; a < 12; a++) {
      const angle = (a / 12) * Math.PI * 2;
      const cx = Math.max(-edge, Math.min(edge, x + Math.cos(angle) * step * 2));
      const cz = Math.max(-edge, Math.min(edge, z + Math.sin(angle) * step * 2));
      if (!isInsideHazard(cx, cz, zones, margin)) return { x: cx, z: cz };
    }
  }
  return null;
}

// ============================================
// SORTEIO
// ============================================

/**
 * PRNG determinístico (LCG) — o mesmo algoritmo do gerador de ondas e do
 * gerador de obstáculos multijogador. Replicado aqui porque este módulo é
 * compartilhado com o servidor e não pode depender de nada do cliente.
 */
export function terrainRandom(seed: number): () => number {
  let s = seed >>> 0;
  if (s === 0) s = 1;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

/** Hash FNV-1a de partes arbitrárias em um seed positivo estável. */
export function terrainSeed(...parts: Array<string | number>): number {
  const key = parts.map(String).join("|");
  let hash = 2166136261;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const normalized = hash & 0x7fffffff;
  return normalized === 0 ? 1 : normalized;
}

export type ArenaArchetype =
  | "open"
  | "lake"
  | "archipelago"
  | "forge"
  | "glacier"
  | "marsh"
  | "crater";

export interface ArchetypeDef {
  id: ArenaArchetype;
  icon: string;
  /**
   * Onda a partir da qual o arquétipo entra no sorteio.
   *
   * Só a Batalha Infinita usa a progressão: lá o jogador aprende uma regra nova
   * de cada vez, água na onda 3, gelo na 6, lava na 9. Nos modos de partida
   * única não existe "onda 1" para proteger — todo mundo já chega sabendo — e o
   * perfil do modo libera o catálogo inteiro.
   */
  minWave: number;
  /** Perigos que o arquétipo usa — alimenta o aviso da abertura de onda. */
  hazards: readonly HazardType[];
}

export const ARCHETYPES: readonly ArchetypeDef[] = Object.freeze([
  { id: "open", icon: "▫️", minWave: 1, hazards: [] },
  { id: "lake", icon: "🌊", minWave: 3, hazards: ["water"] },
  { id: "marsh", icon: "🪵", minWave: 5, hazards: ["mud", "water"] },
  { id: "glacier", icon: "❄️", minWave: 6, hazards: ["ice"] },
  { id: "archipelago", icon: "🏝️", minWave: 8, hazards: ["water"] },
  { id: "forge", icon: "🔥", minWave: 9, hazards: ["lava"] },
  { id: "crater", icon: "🌋", minWave: 12, hazards: ["lava", "ice"] },
]);

const ARCHETYPE_BY_ID = new Map(ARCHETYPES.map((a) => [a.id, a]));

export function getArchetype(id: ArenaArchetype): ArchetypeDef {
  return ARCHETYPE_BY_ID.get(id) ?? ARCHETYPES[0];
}

export interface PickArchetypeInput {
  seed: number;
  /** Até que ponto do catálogo os arquétipos estão liberados. */
  unlock: number;
  /**
   * Arquétipos das últimas arenas. Repetir dentro de uma janela curta é o que
   * fazia a arena parecer sempre a mesma, então eles saem do sorteio quando há
   * alternativa.
   */
  recent?: readonly ArenaArchetype[];
  /**
   * Peso da arena limpa. Uma arena sem perigo de vez em quando dá contraste e
   * faz a próxima pesar mais; num modo de partida única ela também é a válvula
   * que mantém parte das partidas idêntica ao jogo de sempre.
   */
  openWeight?: number;
}

export function pickArchetype({
  seed,
  unlock,
  recent = [],
  openWeight = 22,
}: PickArchetypeInput): ArenaArchetype {
  const random = terrainRandom(seed);
  const unlocked = ARCHETYPES.filter((a) => unlock >= a.minWave);
  if (unlocked.length === 0) return "open";

  const fresh = unlocked.filter((a) => !recent.includes(a.id));
  const pool = fresh.length > 0 ? fresh : unlocked;

  const weightOf = (a: ArchetypeDef) => (a.id === "open" ? openWeight : 100);
  let total = 0;
  for (const a of pool) total += weightOf(a);
  if (total <= 0) return pool[0].id;

  let roll = random() * total;
  for (const a of pool) {
    roll -= weightOf(a);
    if (roll <= 0) return a.id;
  }
  return pool[pool.length - 1].id;
}

// ============================================
// GERAÇÃO
// ============================================

/** Raio máximo ao redor do spawn central que nunca recebe perigo. */
export const SPAWN_SAFE_RADIUS = 9;

/**
 * Raio de segurança efetivo, proporcional à arena.
 *
 * O valor fixo de 9 é bom numa arena de 60, mas numa de 35 ele cobre mais da
 * metade do raio útil: sobrava um anel estreito demais para qualquer mancha
 * caber, e as arenas pequenas — justamente as das primeiras ondas — nasciam
 * sem perigo nenhum apesar de anunciarem "Lago Partido".
 */
export function spawnSafeRadiusFor(arenaSize: number): number {
  return Math.min(SPAWN_SAFE_RADIUS, Math.max(4.5, arenaSize * 0.16));
}

/**
 * Raio mínimo de uma mancha.
 *
 * Abaixo disto ela é pequena demais para ser lida como terreno: o tanque
 * atravessa sem perceber que atravessou, e o que se vê é sujeira no chão.
 */
const MIN_BLOB_RADIUS = 1.8;

/** Reduções tentadas quando a mancha pedida não acha lugar. */
const BLOB_SHRINK_STEPS: readonly number[] = [0.7, 0.45, 0.3];

/** Uma mancha pedida pela receita do arquétipo. */
interface Blob {
  type: HazardType;
  /** Raio base antes da intensidade e da escala da arena. */
  radius: number;
  /** Raio somado depois da escala — piso mínimo para a mancha ser legível. */
  bonus: number;
  lobes: number;
  /** Probabilidade de a mancha existir (1 = sempre). */
  chance?: number;
  /** Quantas manchas iguais a receita pede. */
  count?: number;
}

/**
 * A receita de cada arquétipo.
 *
 * Vinha de um `switch` com números soltos; virou tabela porque agora quatro
 * modos leem a mesma receita e multiplicam por perfis diferentes. Com o
 * `switch`, "o BR tem manchas maiores" viraria um segundo `switch`.
 */
const ARCHETYPE_RECIPES: Record<ArenaArchetype, readonly Blob[]> = {
  open: [],
  lake: [
    { type: "water", radius: 7, bonus: 3, lobes: 3 },
    { type: "water", radius: 5, bonus: 2, lobes: 2, chance: 0.55 },
  ],
  archipelago: [{ type: "water", radius: 4.5, bonus: 2, lobes: 2, count: 4 }],
  marsh: [
    { type: "mud", radius: 7, bonus: 3, lobes: 3 },
    { type: "mud", radius: 5, bonus: 2, lobes: 2 },
    { type: "water", radius: 4, bonus: 2, lobes: 1, chance: 0.6 },
  ],
  glacier: [
    { type: "ice", radius: 8, bonus: 3, lobes: 3 },
    { type: "ice", radius: 6, bonus: 2, lobes: 2, chance: 0.65 },
  ],
  // Lava é sempre menor que os demais perigos: atalho caro, não campo minado.
  forge: [
    { type: "lava", radius: 4.2, bonus: 1.8, lobes: 2 },
    { type: "lava", radius: 3.4, bonus: 1.5, lobes: 1, chance: 0.6 },
  ],
  crater: [
    { type: "lava", radius: 4, bonus: 1.8, lobes: 2 },
    { type: "ice", radius: 6, bonus: 2.5, lobes: 2 },
  ],
};

export interface GenerateHazardsInput {
  archetype: ArenaArchetype;
  arenaSize: number;
  seed: number;
  /** 0..1 — quanto da receita cheia a arena recebe. */
  intensity: number;
  /** Quantas vezes a receita inteira é repetida (arenas grandes pedem mais). */
  repeat?: number;
  /**
   * Multiplicador do tamanho das manchas. Sem valor, acompanha a arena — que é
   * o comportamento da Batalha Infinita, onde a arena cresce devagar.
   */
  blobScale?: number;
  /**
   * Círculos que a geração não pode cobrir.
   *
   * Quando a lista vem preenchida, ela é a autoridade: o raio de spawn central
   * deixa de ser aplicado por cima, porque quem passou a lista já sabe onde os
   * tanques nascem — e nem sempre é no meio. Somar os dois foi o que fez a
   * arena multijogador de 40 unidades nascer sempre lisa: as nove zonas de
   * nascimento engoliam exatamente o anel que o raio central deixava livre, e
   * nenhuma mancha tinha onde caber.
   */
  safeSpots?: readonly SafeSpot[];
}

/**
 * Gera as zonas de uma arena.
 *
 * Restrições que a geração respeita, todas por segurança de jogabilidade:
 *  - nada dentro do raio de spawn central, nem dentro dos spawns extras;
 *  - nada encostando na parede (a arena tem de continuar contornável a pé);
 *  - lava nunca cobre mais que uma fração da arena.
 */
export function generateHazardZones({
  archetype,
  arenaSize,
  seed,
  intensity,
  repeat = 1,
  blobScale,
  safeSpots = [],
}: GenerateHazardsInput): HazardZone[] {
  const recipe = ARCHETYPE_RECIPES[archetype];
  if (!recipe || recipe.length === 0) return [];

  const random = terrainRandom(seed);
  const zones: HazardZone[] = [];
  const bound = arenaSize / 2 - 3.5;
  const safeRadius = safeSpots.length > 0 ? 0 : spawnSafeRadiusFor(arenaSize);

  /**
   * Maior mancha que cabe inteira no anel entre o spawn e a parede.
   *
   * A mancha ocupa de `dist - r` a `dist + r`, então precisa de
   * `dist - r >= safe` e `dist + r <= bound` ao mesmo tempo — o que só existe
   * se `r <= (bound - safe) / 2`. A folga de 0,25 mantém o intervalo de
   * sorteio aberto em vez de degenerar num único ponto.
   */
  const maxFittingRadius = Math.max(0, (bound - safeRadius) / 2 - 0.25);

  const hitsSafeSpot = (x: number, z: number, radius: number): boolean => {
    for (const spot of safeSpots) {
      const dx = x - spot.x;
      const dz = z - spot.z;
      const r = spot.radius + radius;
      if (dx * dx + dz * dz < r * r) return true;
    }
    return false;
  };

  /**
   * Sorteia um centro válido.
   *
   * A faixa de distâncias já é a faixa em que a mancha inteira cabe, então toda
   * tentativa passa pelo teste do anel por construção; as tentativas repetidas
   * existem só por causa dos spawns extras (bots do BR, cantos do
   * multijogador), que nem toda arena tem. Sem nenhum spawn extra — o caso da
   * Batalha Infinita — a primeira tentativa sempre vale, e o sorteio é o mesmo
   * de antes.
   */
  const placeCenter = (radius: number): { x: number; z: number } | null => {
    const minDist = safeRadius + radius;
    const maxDist = bound - radius;
    if (maxDist <= minDist) return null;

    for (let attempt = 0; attempt < 24; attempt++) {
      const angle = random() * Math.PI * 2;
      const dist = minDist + random() * (maxDist - minDist);
      const x = Math.cos(angle) * dist;
      const z = Math.sin(angle) * dist;
      if (!hitsSafeSpot(x, z, radius)) return { x, z };
    }
    return null;
  };

  const addBlob = (type: HazardType, requestedRadius: number, lobes: number) => {
    // Encolhe para caber em vez de desistir: numa arena apertada a mancha fica
    // menor, mas a arena cumpre o que a abertura de onda prometeu.
    const wanted = Math.min(requestedRadius, maxFittingRadius);
    // Abaixo disso a mancha é pequena demais para ser lida como terreno — seria
    // uma poça que o tanque atravessa sem perceber.
    if (wanted < MIN_BLOB_RADIUS) return;

    /**
     * Encolher também vale contra os nascimentos, não só contra a parede.
     *
     * `maxFittingRadius` só sabe do anel entre o centro e a parede; quem
     * inviabiliza a mancha nas arenas pequenas do multijogador são as nove
     * áreas de nascimento, e disso só `placeCenter` sabe. Sem estas tentativas
     * o sorteio pedia uma mancha grande, não achava onde pôr, e desistia em
     * silêncio — a arena de 40 unidades nascia sempre lisa. Uma arena pequena
     * merece uma poça pequena, não nenhuma.
     */
    let baseRadius = wanted;
    let center = placeCenter(baseRadius);
    for (const shrink of BLOB_SHRINK_STEPS) {
      if (center) break;
      const smaller = wanted * shrink;
      if (smaller < MIN_BLOB_RADIUS) break;
      baseRadius = smaller;
      center = placeCenter(baseRadius);
    }
    if (!center) return;
    zones.push({ type, x: center.x, z: center.z, radius: baseRadius });
    // Lóbulos menores em volta quebram o círculo perfeito e dão contorno
    // orgânico sem custar nada na consulta.
    for (let i = 0; i < lobes; i++) {
      const angle = random() * Math.PI * 2;
      const offset = baseRadius * (0.5 + random() * 0.45);
      const lobeRadius = baseRadius * (0.45 + random() * 0.35);
      const x = center.x + Math.cos(angle) * offset;
      const z = center.z + Math.sin(angle) * offset;
      if (Math.hypot(x, z) - lobeRadius < safeRadius) continue;
      // Contra `bound`, não contra a parede: encostar na parede fazia o lóbulo
      // transbordar o piso desenhado e a mancha aparecia derramando no vazio.
      if (Math.hypot(x, z) + lobeRadius > bound) continue;
      if (hitsSafeSpot(x, z, lobeRadius)) continue;
      zones.push({ type, x, z, radius: lobeRadius });
    }
  };

  const scale = blobScale ?? arenaSize / 44;
  const rounds = Math.max(1, Math.round(repeat));

  for (let round = 0; round < rounds; round++) {
    for (const blob of recipe) {
      const count = blob.count ?? 1;
      for (let i = 0; i < count; i++) {
        if (blob.chance !== undefined && random() > blob.chance) continue;
        addBlob(blob.type, blob.radius * scale * intensity + blob.bonus, blob.lobes);
      }
    }
  }

  return zones;
}

/** Perigos presentes nas zonas, para o aviso da abertura de onda. */
export function hazardsPresent(zones: readonly HazardZone[]): HazardType[] {
  const seen = new Set<HazardType>();
  for (const zone of zones) seen.add(zone.type);
  return Array.from(seen);
}

// ============================================
// O PLANO POR MODO
// ============================================

export type TerrainMode =
  | "infinite"
  | "battleRoyale"
  | "multiplayer"
  | "genai"
  | "story"
  | "dungeon";

interface ModeProfile {
  /**
   * `false` mantém a arena inteiramente lisa. História e masmorra são conteúdo
   * roteirizado: as salas foram desenhadas com posições exatas de bloco, porta
   * e diálogo, e um lago sorteado por cima pode tornar um objetivo impossível.
   */
  enabled: boolean;
  /**
   * De onde vem o nível de liberação dos arquétipos. `"wave"` usa a onda (só a
   * Batalha Infinita tem progressão); `"full"` libera o catálogo inteiro.
   */
  progression: "wave" | "full";
  /**
   * Peso da arena limpa no sorteio, contra 100 de cada arquétipo com perigo.
   *
   * Com os seis arquétipos liberados, 60 aqui significa uma arena limpa a cada
   * onze — o suficiente para o chão liso continuar acontecendo e dar contraste,
   * sem transformar o terreno em exceção rara.
   */
  openWeight: number;
  /** Intensidade da receita quando não há onda para escalar. */
  intensity: number;
  /**
   * `true` repete a receita proporcionalmente ao tamanho da arena.
   *
   * A receita foi calibrada para as arenas de ~44 unidades da Batalha Infinita.
   * A arena de Battle Royale começa em 120 e mais: sem repetir, o mapa inteiro
   * teria um laguinho perdido num canto. A Infinita não repete, porque lá o
   * crescimento da arena já é acompanhado pelo crescimento da intensidade.
   */
  scaleWithArena: boolean;
}

/**
 * O único lugar em que um modo difere de outro.
 *
 * Tudo o que vem depois desta tabela — geração, dano, fogo que gruda, rastro no
 * chão, IA que desvia da lava — é idêntico em qualquer partida. É isso que faz
 * "atravessei um lago" significar a mesma coisa jogando sozinho, com amigos ou
 * num Battle Royale de dezesseis.
 */
const MODE_PROFILES: Record<TerrainMode, ModeProfile> = {
  infinite: {
    enabled: true,
    progression: "wave",
    openWeight: 22,
    intensity: 0.75,
    scaleWithArena: false,
  },
  // Mapa grande e partida longa: o terreno é o que quebra a monotonia do
  // corredor de blocos, e a zona que encolhe já obriga a atravessá-lo.
  battleRoyale: {
    enabled: true,
    progression: "full",
    openWeight: 12,
    intensity: 0.8,
    scaleWithArena: true,
  },
  // Partida curta contra gente de verdade. O peso de arena limpa é o dobro do
  // da Batalha Infinita: numa partida de cinco minutos, cair sempre num mapa
  // com perigo tira a chance de jogar a sala "clássica" de vez em quando.
  //
  // Vale registrar uma consequência geométrica: as arenas clássicas do
  // multijogador são pequenas (16 a 50 unidades) e têm nove áreas de
  // nascimento, então as manchas saem encolhidas para caber entre elas — poças
  // de dois ou três de raio em vez do lago de sete da Batalha Infinita. É a
  // proporção certa: arena pequena, poça pequena. Abaixo de ~30 unidades não
  // sobra espaço para nada legível e a partida sai lisa.
  multiplayer: {
    enabled: true,
    progression: "full",
    openWeight: 60,
    intensity: 0.7,
    scaleWithArena: true,
  },
  // Duelo contra a IA generativa, em arena pequena e com dois combatentes: a
  // intensidade é a menor do conjunto para a poça continuar sendo uma opção de
  // rota, e não o assunto do duelo inteiro.
  genai: {
    enabled: true,
    progression: "full",
    openWeight: 80,
    intensity: 0.5,
    scaleWithArena: true,
  },
  story: {
    enabled: false,
    progression: "full",
    openWeight: 100,
    intensity: 0,
    scaleWithArena: false,
  },
  dungeon: {
    enabled: false,
    progression: "full",
    openWeight: 100,
    intensity: 0,
    scaleWithArena: false,
  },
};

export interface TerrainPlanInput {
  mode: TerrainMode;
  /** Semente determinística: mesmo valor ⇒ mesma arena em todos os peers. */
  seed: number;
  arenaSize: number;
  /** Onda atual. Só a Batalha Infinita usa. */
  wave?: number;
  /** Spawns que a geração precisa respeitar, além do centro. */
  safeSpots?: readonly SafeSpot[];
  /** Arquétipos das arenas recentes, para não repetir. */
  recentArchetypes?: readonly ArenaArchetype[];
  /** Arena de chefe: menos manchas, para o padrão de ataque ficar legível. */
  calmArena?: boolean;
  /** Desliga o terreno mesmo num modo que o suporta (sala roteirizada). */
  disabled?: boolean;
}

export interface TerrainPlan {
  archetype: ArenaArchetype;
  zones: HazardZone[];
  hazards: HazardType[];
}

const EMPTY_PLAN: TerrainPlan = { archetype: "open", zones: [], hazards: [] };

/**
 * Decide a arena de uma partida — em qualquer modo.
 *
 * Ponto de entrada único de propósito: enquanto cada modo montava o próprio
 * terreno, "terreno" queria dizer uma coisa na Batalha Infinita e nada em todo
 * o resto. Com um plano só, acrescentar um modo é acrescentar uma linha em
 * `MODE_PROFILES`, e corrigir a geração corrige todos de uma vez.
 */
export function planArenaTerrain(input: TerrainPlanInput): TerrainPlan {
  const profile = MODE_PROFILES[input.mode];
  if (!profile || !profile.enabled || input.disabled) return EMPTY_PLAN;

  const wave = input.wave ?? 1;

  // As duas primeiras ondas da Infinita são sempre limpas: é onde se aprende a
  // atirar. Nos modos de partida única não existe equivalente.
  if (profile.progression === "wave" && wave <= 2) return EMPTY_PLAN;

  const unlock = profile.progression === "wave" ? wave : Number.MAX_SAFE_INTEGER;
  const archetype = pickArchetype({
    seed: terrainSeed("archetype", input.seed),
    unlock,
    recent: input.recentArchetypes,
    openWeight: profile.openWeight,
  });

  if (archetype === "open") return EMPTY_PLAN;

  // Perigo cresce com a onda, mas satura cedo: a partir daí a variedade vem do
  // arquétipo, não de cobrir o chão de lava. Sem ondas, vale o perfil do modo.
  const intensity =
    profile.progression === "wave"
      ? Math.min(1, 0.45 + wave * 0.03)
      : profile.intensity;

  // A receita foi calibrada para ~44 unidades. Arenas maiores recebem a receita
  // **repetida**, com a mancha mais ou menos do mesmo tamanho — crescer o raio
  // junto com a arena somava dois crescimentos e a cobertura ia a 60% do mapa
  // num Battle Royale de 200: não é mais terreno, é outro jogo, com uma parede
  // de lava no meio. Mais poças pequenas espalhadas dão rota alternativa; uma
  // poça gigante só bloqueia.
  const repeat = profile.scaleWithArena
    ? Math.max(1, Math.min(4, Math.round(input.arenaSize / 55)))
    : 1;
  const blobScale = profile.scaleWithArena
    ? Math.max(0.8, Math.min(1.4, input.arenaSize / 44))
    : undefined;

  const zones = generateHazardZones({
    archetype,
    arenaSize: input.arenaSize,
    seed: terrainSeed("zones", input.seed),
    intensity,
    repeat,
    blobScale,
    safeSpots: input.safeSpots,
  });

  // Arena de chefe perde manchas em vez de nascer sem nenhuma: o padrão de
  // ataque precisa de espaço para ser lido, mas a arena anunciada continua
  // sendo a que aparece.
  if (input.calmArena && zones.length > 2) {
    zones.length = Math.max(2, zones.length - 2);
  }

  if (zones.length === 0) return EMPTY_PLAN;

  return { archetype, zones, hazards: hazardsPresent(zones) };
}
